import { RewardWithdrawalContextType } from './reward-withdrawal-context.type';
import { createContext, ReactNode, useContext } from 'react';
import { Outlet } from 'react-router-dom';
import { useRewardWithdrawalDetails } from 'src/hooks/use-reward-withdrawal-details';
import i18n from 'src/i18n';

// Create the user stats context with an initial undefined value.
const RewardWithdrawalContext = createContext<RewardWithdrawalContextType | undefined>(undefined);

/**
 * RewardWithdrawalProvider component wraps the application and provides reward withdrawal context.
 * @param children - The components that will have access to the reward withdrawal context.
 */
export function RewardWithdrawalProvider({ children }: { children?: ReactNode }) {
  const rewardWithdrawal = useRewardWithdrawalDetails(); // Retrieve reward withdrawal state and methods from the custom hook.

  // Provide the reward withdrawal context to the component tree.
  return (
    <RewardWithdrawalContext.Provider value={rewardWithdrawal}>
      {children || <Outlet />}
    </RewardWithdrawalContext.Provider>
  );
}

/**
 * Custom hook to access the reward withdrawal context.
 * Throws an error if used outside of an RewardWithdrawalProvider.
 */
export function useRewardWithdrawalContext() {
  const context = useContext(RewardWithdrawalContext); // Access the reward withdrawal context.

  // Throw an error if the hook is used outside of an RewardWithdrawalProvider.
  if (!context) {
    throw new Error(i18n.t('ERRORS.CONTEXT.REWARD_WITHDRAWAL'));
  }

  return context; // Return the context value if it exists.
}
